import React, { useState, useEffect } from "react";
import { useSelector } from 'react-redux';
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import moment from "moment";
import profileImage from "../../../../images/user/Default_pfp.png";
import TruncateUserName from "../../../helpers/truncateUserName";
import DateConverter from "../../../../helpers/dateFormatChange";

// Dummy data for testing
const dummyData = [
  {
    user: {
      name: "Rey Bugawan",
      profilePhoto: profileImage,
    },
    type: "Personal",
    issueDate: "2023-07-01",
    totalDays: 5,
    startDate: "2023-07-10",
    endDate: "2023-07-14",
  },
  {
    user: {
      name: "Calin Tatar",
      profilePhoto: profileImage,
    },
    type: "Medical",
    issueDate: "2023-07-01",
    totalDays: 0.5,
    startDate: "2023-07-10",
    endDate: "2023-07-14",
  },
  {
    user: {
      name: "Jane Smith",
      profilePhoto: profileImage,
    },
    type: "Sick Leave",
    issueDate: "2023-07-02",
    totalDays: 3,
    startDate: "2023-07-15",
    endDate: "2023-07-17",
  },
  // Add more dummy data as needed
];

const TimeOffRequestTable = ({ timeoffReq, timeoffReqLoading, timeoffReqError }) => {
  const reportingTimeoffRequest = useSelector((state) => state.reportingTimeoffRequest);
  const [filteredData, setFilteredData] = useState([]);

  useEffect(() => {
    if (timeoffReq && reportingTimeoffRequest) {
      const filtered = timeoffReq.filter(tor =>
        !tor.is_delete &&
        reportingTimeoffRequest.timeoffReqUser && reportingTimeoffRequest.timeoffReqUser.includes(tor.user._id) &&
        reportingTimeoffRequest.timeoffReqReqStatus && reportingTimeoffRequest.timeoffReqReqStatus.includes(tor.status) &&
        reportingTimeoffRequest.timeoffReqYear && new Date(tor?.end_date).getFullYear() === reportingTimeoffRequest.timeoffReqYear &&
        reportingTimeoffRequest.timeoffReqCategory && reportingTimeoffRequest.timeoffReqCategory.includes(tor.type_of_leave._id)
      );
      const sortedData = filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setFilteredData(sortedData);
    }
  }, [timeoffReq, reportingTimeoffRequest]);

  const renderUser = (rowData) => {
    const userDetails = rowData.user;
    const fullName = userDetails.first_name ? userDetails.first_name +
      (userDetails.middle_name && userDetails.middle_name !== "null" ? " " + userDetails.middle_name : "") +
      " " +
      userDetails.last_name
      : userDetails.company_name;
    return (
      <div className='member-name' style={{ display: "flex", alignItems: "center" }} >
        {userDetails.profile_image !== null && userDetails.profile_image !== "null" ? (
          <img
            src={`/uploads/lowProfileImage/${userDetails.profile_image}`}
            alt={fullName}
            className="member-image"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/uploads/lowProfileImage/user_default.png';
            }}
          />
        ) : (
          <img src={profileImage} alt={fullName} className="member-image" />
        )}
        <TruncateUserName
          textContent={userDetails.first_name ? (
            <>
              {userDetails.first_name}{' '}
              {userDetails.middle_name && userDetails.middle_name !== 'null' ? userDetails.middle_name + ' ' : ''}
              {userDetails.last_name || ''}
            </>
          ) : (
            <>
              {userDetails.company_first_name
                ? `${userDetails.company_first_name} ${userDetails.company_last_name ? userDetails.company_last_name : ''}`.trim()
                : `${userDetails.company_name || ''}(Company)`}
            </>
          )} />
      </div>
    );
  };

  const calculateNumberOfDays = (startDate, startTime, endDate, endTime) => {
    if (!startTime || !endTime || startDate !== endDate) {
      const startDateTime = moment(startDate).tz(global.config.getTimeZone()).startOf('day');
      const endDateTime = moment(endDate).tz(global.config.getTimeZone()).startOf('day');
      const duration = moment.duration(endDateTime.diff(startDateTime));
      const numberOfDays = Math.ceil(duration.asDays());
      return `${numberOfDays + 1} days`;
    } else {
      const startDateTime = moment(startDate).tz(global.config.getTimeZone()).startOf('day').add(startTime, 'hours');
      const endDateTime = moment(endDate).tz(global.config.getTimeZone()).startOf('day').add(endTime, 'hours');
      const duration = moment.duration(endDateTime.diff(startDateTime));
      const numberOfDays = Math.ceil(duration.asDays());
      if (numberOfDays === 1) {
        const numberOfHours = (endTime - startTime) * numberOfDays;
        const numberOfDaysDecimal = (numberOfHours / 8).toFixed(2);
        const cappedNumberOfDaysDecimal = Math.min(numberOfDaysDecimal, 1);
        return `${cappedNumberOfDaysDecimal} days`;
      } else {
        return `${numberOfDays} days`;
      }
    }
  };

  if (timeoffReqLoading) {
    return (
      <div className="datatable-team-maintb">
        <div
          className="p-datatable p-component p-datatable-responsive-scroll"
          style={{ marginBottom: 0, paddingBottom: 0 }}
        >
          <div 
            className="p-datatable-wrapper"
            style={{ paddingBottom: 0, marginBottom: 0 }}
          >
            <table
              className="p-datatable-table"
              style={{ 
                minWidth: "50rem", 
                width: "100%", 
                textAlign: "left",
                marginBottom: 0,
                borderSpacing: 0,
              }}
            >
              <thead className="p-datatable-thead">
                <tr>
                  <th style={{ 
                    paddingRight: "20px", 
                    textAlign: "left", 
                    paddingLeft: "22px",
                    paddingTop: "11px",
                    paddingBottom: "12px",
                    background: "#F5F6F8",
                    fontWeight: 500,
                    fontSize: "17px",
                    lineHeight: "21px",
                    color: "#6A7988",
                    borderTopLeftRadius: "10px",
                    borderBottomLeftRadius: "10px",
                    width: "20%"
                  }}>
                    User
                  </th>
                  <th style={{ 
                    paddingRight: "20px", 
                    textAlign: "left", 
                    paddingTop: "11px",
                    paddingBottom: "12px",
                    background: "#F5F6F8",
                    fontWeight: 500,
                    fontSize: "17px",
                    lineHeight: "21px",
                    color: "#6A7988",
                    width: "15%"
                  }}>
                    Type
                  </th>
                  <th style={{ 
                    paddingRight: "20px", 
                    textAlign: "left", 
                    paddingTop: "11px",
                    paddingBottom: "12px",
                    background: "#F5F6F8",
                    fontWeight: 500,
                    fontSize: "17px",
                    lineHeight: "21px",
                    color: "#6A7988",
                    width: "15%"
                  }}>
                    Issue Date
                  </th>
                  <th style={{ 
                    paddingRight: "20px", 
                    textAlign: "left", 
                    paddingTop: "11px",
                    paddingBottom: "12px",
                    background: "#F5F6F8",
                    fontWeight: 500,
                    fontSize: "17px",
                    lineHeight: "21px",
                    color: "#6A7988",
                    width: "10%"
                  }}>
                    Total Days
                  </th>
                  <th style={{ 
                    paddingRight: "20px", 
                    textAlign: "left", 
                    paddingTop: "11px",
                    paddingBottom: "12px",
                    background: "#F5F6F8",
                    fontWeight: 500,
                    fontSize: "17px",
                    lineHeight: "21px",
                    color: "#6A7988",
                    width: "15%"
                  }}>
                    Start Date
                  </th>
                  <th style={{ 
                    paddingRight: "22px", 
                    textAlign: "left", 
                    paddingTop: "11px",
                    paddingBottom: "12px",
                    background: "#F5F6F8",
                    fontWeight: 500,
                    fontSize: "17px",
                    lineHeight: "21px",
                    color: "#6A7988",
                    borderTopRightRadius: "10px",
                    borderBottomRightRadius: "10px",
                    width: "15%"
                  }}>
                    End Date
                  </th>
                </tr>
              </thead>
              <tbody className="p-datatable-tbody" style={{ marginBottom: 0, paddingBottom: 0 }}>
                {Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`skeleton-${index}`}>
                    {/* User column */}
                    <td style={{ 
                      paddingRight: "20px", 
                      paddingLeft: "22px",
                      paddingTop: "19px",
                      paddingBottom: "22px",
                      borderBottom: "0.5px solid #d8dde1"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            backgroundColor: "#e5e7eb",
                            animation: "pulse 1.5s ease-in-out infinite",
                            flexShrink: 0
                          }}
                        />
                        <div
                          style={{
                            width: "120px",
                            height: "20px",
                            backgroundColor: "#e5e7eb",
                            borderRadius: "4px",
                            animation: "pulse 1.5s ease-in-out infinite",
                          }}
                        />
                      </div>
                    </td>
                    {/* Type column */}
                    <td style={{ 
                      paddingRight: "20px", 
                      paddingTop: "19px",
                      paddingBottom: "22px",
                      borderBottom: "0.5px solid #d8dde1"
                    }}>
                      <div
                        style={{
                          width: "80px",
                          height: "20px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "4px",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      />
                    </td>
                    {/* Issue Date column */}
                    <td style={{ 
                      paddingRight: "20px", 
                      paddingTop: "19px",
                      paddingBottom: "22px",
                      borderBottom: "0.5px solid #d8dde1"
                    }}>
                      <div
                        style={{
                          width: "100px",
                          height: "20px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "4px",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      />
                    </td>
                    {/* Total Days column */}
                    <td style={{ 
                      paddingRight: "20px", 
                      paddingTop: "19px",
                      paddingBottom: "22px",
                      borderBottom: "0.5px solid #d8dde1"
                    }}>
                      <div
                        style={{
                          width: "60px",
                          height: "20px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "4px",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      />
                    </td>
                    {/* Start Date column */}
                    <td style={{ 
                      paddingRight: "20px", 
                      paddingTop: "19px",
                      paddingBottom: "22px",
                      borderBottom: "0.5px solid #d8dde1"
                    }}>
                      <div
                        style={{
                          width: "100px",
                          height: "20px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "4px",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      />
                    </td>
                    {/* End Date column */}
                    <td style={{ 
                      paddingRight: "22px", 
                      paddingTop: "19px",
                      paddingBottom: "22px",
                      borderBottom: "0.5px solid #d8dde1"
                    }}>
                      <div
                        style={{
                          width: "100px",
                          height: "20px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "4px",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="datatable-team-maintb">
      <DataTable
        value={filteredData.slice(0, 5)}
        emptyMessage={
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 24px", gap: "10px" }}>
            <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="6" y="10" width="32" height="26" rx="3" stroke="#D0D5DD" strokeWidth="2" />
              <path d="M6 16h32" stroke="#D0D5DD" strokeWidth="2" />
              <path d="M14 7v6M30 7v6" stroke="#D0D5DD" strokeWidth="2" strokeLinecap="round" />
              <path d="M13 24h6M13 29h12" stroke="#BDBDBD" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <p style={{ margin: 0, fontSize: "15px", fontWeight: "500", color: "#282828" }}>No time off requests found</p>
            <p style={{ margin: 0, fontSize: "13px", color: "#96A8BA" }}>Try adjusting your filters.</p>
          </div>
        }
        tableStyle={{ minWidth: "50rem", width: "100%", textAlign: "left" }}
      >
        <Column
          field="user.first_name"
          header="User"
          sortable
          body={renderUser}
          style={{ width: "20%", paddingRight: "20px" }}
        />
        <Column
          field="type_of_leave.leave_type"
          header="Type"
          sortable
          style={{ width: "15%", paddingRight: "20px" }}
        />
        <Column
          field="created_at"
          header="Issue Date"
          sortable
          body={(rowData) =>
            <DateConverter date={rowData.created_at} />
          }
          style={{ width: "15%", paddingRight: "20px" }}
        />
        <Column
          field="totalDays"
          header="Total Days"
          body={(rowData) => calculateNumberOfDays(
            rowData.start_date,
            rowData.start_time,
            rowData.end_date,
            rowData.end_time
          )}
          style={{ width: "10%", paddingRight: "20px" }}
        />
        <Column
          field="start_date"
          header="Start Date"
          sortable
          body={(rowData) =>
            <DateConverter date={rowData.start_date} />
          }
          style={{ width: "15%", paddingRight: "20px" }}
        />
        <Column
          field="end_date"
          header="End Date"
          sortable
          body={(rowData) =>
            <DateConverter date={rowData.end_date} />
          }
          style={{ width: "15%", paddingRight: "20px" }}
        />
      </DataTable>
    </div>
  );
};

export default TimeOffRequestTable;
